import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../../utils/constants';

const InstallationManualsModule = ({ onBack, currentUser }) => {
  const [manuals, setManuals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredManuals, setFilteredManuals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPushPierForm, setShowPushPierForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedManual, setSelectedManual] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    filename: '',
    file_data: '',
    category: 'General',
    description: '',
    uploaded_by: ''
  });
  const [emailForm, setEmailForm] = useState({
    recipient_email: '',
    sender_name: '',
    message: ''
  });
  const [editForm, setEditForm] = useState({
    title: '',
    category: '',
    description: ''
  });

  const categories = [
    'General',
    'SmartJack Installation',
    'Utility Installation',
    'Safety Procedures',
    'Equipment Manuals',
    'Regulatory Compliance',
    'Site Preparation',
    'Maintenance',
    'Troubleshooting'
  ];

  React.useEffect(() => {
    fetchManuals();
  }, []);

  React.useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredManuals(manuals);
    } else {
      const filtered = manuals.filter(manual =>
        manual.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        manual.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        manual.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        manual.filename.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredManuals(filtered);
    }
  }, [manuals, searchQuery]);

  const fetchManuals = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/manuals`);
      setManuals(response.data);
    } catch (error) {
      console.error('Error fetching manuals:', error);
      setManuals([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        alert('Please select a PDF file only.');
        return;
      }
      
      // Check file size (limit to 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB.');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Data = e.target.result.split(',')[1]; // Remove data:application/pdf;base64, prefix
        setUploadForm({
          ...uploadForm,
          filename: file.name,
          file_data: base64Data,
          title: uploadForm.title || file.name.replace('.pdf', ''),
          uploaded_by: 'Current User'
        });
        console.log('File uploaded:', file.name, 'Base64 length:', base64Data.length);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/manuals`, uploadForm);
      alert('Installation manual uploaded successfully!');
      
      setUploadForm({
        title: '',
        filename: '',
        file_data: '',
        category: 'General',
        description: '',
        uploaded_by: ''
      });
      setShowUploadForm(false);
      fetchManuals();
    } catch (error) {
      console.error('Error uploading manual:', error);
      alert('Error uploading manual. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/manuals/${selectedManual.id}/send-email`, emailForm);
      alert('Installation manual sent via email successfully!');
      
      setEmailForm({
        recipient_email: '',
        sender_name: '',
        message: ''
      });
      setShowEmailModal(false);
      setSelectedManual(null);
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error sending email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPDF = async (manual) => {
    console.log('handleViewPDF called with manual:', manual);
    
    try {
      const viewUrl = `${API}/manuals/${manual.id}/view`;
      console.log('Opening PDF for viewing:', viewUrl);
      
      // Check if we're on mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isMobile) {
        console.log('Mobile device detected, using mobile-friendly approach');
        
        // For mobile, directly navigate to the PDF URL
        // This works better on mobile browsers
        window.location.href = viewUrl;
        
      } else {
        // Desktop approach with popup handling
        const newWindow = window.open(viewUrl, '_blank');
        
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          console.warn('Popup blocked, using fallback method');
          window.location.href = viewUrl;
        } else {
          console.log('PDF opened successfully in new tab');
        }
      }
      
    } catch (error) {
      console.error('Error viewing PDF:', error);
      
      // Fallback: direct navigation
      try {
        const viewUrl = `${API}/manuals/${manual.id}/view`;
        window.location.href = viewUrl;
      } catch (fallbackError) {
        alert('Error opening PDF. Please try again or contact support.');
      }
    }
  };

  const handleDownloadPDF = async (manual) => {
    console.log('handleDownloadPDF called with manual:', manual);
    
    try {
      const downloadUrl = `${API}/manuals/${manual.id}/download`;
      console.log('Downloading PDF from:', downloadUrl);
      
      // Show loading state on button
      const button = event?.target?.closest('button');
      if (button) {
        const originalText = button.innerHTML;
        button.innerHTML = '⬇️ Downloading...';
        button.disabled = true;
        
        // Reset button after delay
        setTimeout(() => {
          button.innerHTML = '✅ Downloaded!';
          setTimeout(() => {
            button.innerHTML = originalText;
            button.disabled = false;
          }, 2000);
        }, 1500);
      }
      
      // Check if we're on mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isMobile) {
        console.log('Mobile device detected, using mobile-friendly download approach');
        
        try {
          // Method 1: Try to fetch the file and create a blob URL (better for mobile)
          const response = await fetch(downloadUrl);
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          
          // Create download link
          const a = document.createElement('a');
          a.href = url;
          a.download = manual.filename || 'manual.pdf';
          a.style.display = 'none';
          
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          
          // Clean up the blob URL
          window.URL.revokeObjectURL(url);
          
          console.log('Mobile download completed successfully');
          
        } catch (fetchError) {
          console.warn('Fetch method failed on mobile, trying fallback:', fetchError);
          
          // Fallback for mobile: open download URL directly
          window.open(downloadUrl, '_blank');
        }
        
      } else {
        // Desktop approach
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = manual.filename || 'manual.pdf';
        a.style.display = 'none';
        
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        
        console.log('Desktop download triggered successfully');
      }
      
    } catch (error) {
      console.error('Error downloading PDF:', error);
      
      // Ultimate fallback: open download URL in new tab/window
      try {
        console.log('Trying ultimate fallback: opening download URL');
        const downloadUrl = `${API}/manuals/${manual.id}/download`;
        window.open(downloadUrl, '_blank');
      } catch (fallbackError) {
        console.error('All download methods failed:', fallbackError);
        alert('Error downloading PDF. Please try again or contact support.');
      }
      
      // Reset button state
      const button = event?.target?.closest('button');
      if (button) {
        button.innerHTML = '💾 <span class="hidden sm:inline">Download</span>';
        button.disabled = false;
      }
    }
  };

  const handleEmailManual = (manual) => {
    setSelectedManual(manual);
    setEmailForm({
      recipient_email: '',
      sender_name: 'Staff',
      message: `Please find the installation manual "${manual.title}" attached to this email.`
    });
    setShowEmailModal(true);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const handleEditManual = (manual) => {
    setSelectedManual(manual);
    setEditForm({
      title: manual.title,
      category: manual.category,
      description: manual.description || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateManual = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.put(`${API}/manuals/${selectedManual.id}`, editForm);
      alert('Manual updated successfully!');
      
      setEditForm({
        title: '',
        category: '',
        description: ''
      });
      setShowEditModal(false);
      setSelectedManual(null);
      fetchManuals();
    } catch (error) {
      console.error('Error updating manual:', error);
      alert('Error updating manual. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteManual = async () => {
    if (!selectedManual) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API}/manuals/${selectedManual.id}`);
      alert('Manual deleted successfully!');
      
      setShowDeleteModal(false);
      setSelectedManual(null);
      fetchManuals();
    } catch (error) {
      console.error('Error deleting manual:', error);
      alert('Error deleting manual. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteManual = (manual) => {
    setSelectedManual(manual);
    setShowDeleteModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      
      {/* Show Push Pier Form if requested */}
      {showPushPierForm && (
        <PushPierLogForm
          onBack={() => setShowPushPierForm(false)}
          onSave={(data) => {
            console.log('Push Pier Log saved:', data);
            setShowPushPierForm(false);
            alert('Push Pier Log data saved successfully!');
          }}
        />
      )}

      {/* Show main manuals interface if not showing forms */}
      {!showPushPierForm && (
      <div className="container mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <button onClick={onBack} className="text-blue-600 hover:text-blue-700 text-xl self-start">← Back</button>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 text-center">📖 INSTALLATION MANUALS</h1>
          {currentUser?.rawRole === 'admin' ? (
            <button
              onClick={() => setShowUploadForm(true)}
              className="bg-green-600 hover:bg-green-700 px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-bold text-white shadow-sm text-sm sm:text-base"
            >
              📁 UPLOAD
            </button>
          ) : (
            <div />
          )}
        </div>

        {/* Universal Search */}
        <div className="mb-6 sm:mb-8 bg-white rounded-xl p-4 sm:p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="🔍 Search manuals..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-3 sm:p-4 pl-10 sm:pl-12 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
              />
              <div className="absolute left-3 sm:left-4 top-3 sm:top-4 text-gray-400 text-lg sm:text-xl">🔍</div>
            </div>
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="bg-gray-500 hover:bg-gray-600 px-4 py-3 rounded-lg text-white font-semibold text-sm sm:text-base"
              >
                Clear
              </button>
            )}
          </div>
          
          {searchQuery && (
            <div className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-600">
              Found {filteredManuals.length} manual{filteredManuals.length !== 1 ? 's' : ''} matching "{searchQuery}"
            </div>
          )}
        </div>

        {/* Manuals List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-6xl mb-4">📚</div>
              <p>Loading installation manuals...</p>
            </div>
          ) : filteredManuals.length === 0 && searchQuery ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-6xl mb-4">🔍</div>
              <p>No manuals found matching "{searchQuery}"</p>
              <p className="text-sm">Try different search terms</p>
            </div>
          ) : filteredManuals.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-6xl mb-4">📖</div>
              <p>No installation manuals uploaded yet.</p>
              <p className="text-sm">Click "Upload Manual" to get started</p>
            </div>
          ) : (
            filteredManuals.map((manual) => (
              <div key={manual.id} className="bg-white rounded-xl p-4 md:p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
                
                {/* Manual Info */}
                <div className="mb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                    <h3 className="text-lg md:text-xl font-bold text-gray-800 leading-tight">{manual.title}</h3>
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold w-fit">
                      {manual.category}
                    </span>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs md:text-sm text-gray-600 mb-3">
                    <span className="flex items-center gap-1">
                      📄 <strong>{manual.filename}</strong>
                    </span>
                    <span className="flex items-center gap-1">
                      👤 {manual.uploaded_by}
                    </span>
                    <span className="flex items-center gap-1">
                      📅 {new Date(manual.created_date).toLocaleDateString()}
                    </span>
                  </div>

                  {manual.description && (
                    <p className="text-gray-700 text-sm md:text-base">{manual.description}</p>
                  )}
                </div>

                {/* Mobile-Friendly Action Buttons */}
                <div className={`grid gap-2 ${manual.title === 'Push Pier Log' ? 'grid-cols-3 sm:grid-cols-6' : 'grid-cols-3 sm:grid-cols-5'}`}>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleViewPDF(manual);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 px-3 py-2 rounded-lg text-white font-semibold text-xs flex items-center justify-center gap-1 touch-manipulation"
                    title={`View ${manual.title}`}
                    style={{ minHeight: '44px' }}
                  >
                    👁️ <span className="hidden sm:inline">View</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDownloadPDF(manual);
                    }}
                    className="bg-green-600 hover:bg-green-700 active:bg-green-800 px-3 py-2 rounded-lg text-white font-semibold text-xs flex items-center justify-center gap-1 touch-manipulation"
                    title={`Download ${manual.title}`}
                    style={{ minHeight: '44px' }}
                  >
                    💾 <span className="hidden sm:inline">Download</span>
                  </button>
                  {/* Special Fill Out button for Push Pier Log */}
                  {manual.title === 'Push Pier Log' && (
                    <button
                      onClick={() => setShowPushPierForm(true)}
                      className="bg-orange-600 hover:bg-orange-700 px-3 py-2 rounded-lg text-white font-semibold text-xs flex items-center justify-center gap-1"
                    >
                      ✏️ <span className="hidden sm:inline">Fill Out</span>
                    </button>
                  )}
                  <button
                    onClick={() => handleEmailManual(manual)}
                    className="bg-purple-600 hover:bg-purple-700 px-3 py-2 rounded-lg text-white font-semibold text-xs flex items-center justify-center gap-1"
                  >
                    📧 <span className="hidden sm:inline">Email</span>
                  </button>
                  {currentUser?.rawRole === 'admin' ? (
                    <>
                      <button
                        onClick={() => handleEditManual(manual)}
                        className="bg-amber-600 hover:bg-amber-700 px-3 py-2 rounded-lg text-white font-semibold text-xs flex items-center justify-center gap-1"
                      >
                        ✏️ <span className="hidden sm:inline">Edit</span>
                      </button>
                      <button
                        onClick={() => confirmDeleteManual(manual)}
                        className="bg-red-600 hover:bg-red-700 px-3 py-2 rounded-lg text-white font-semibold text-xs flex items-center justify-center gap-1"
                      >
                        🗑️ <span className="hidden sm:inline">Delete</span>
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Upload Manual Modal */}
        {currentUser?.rawRole === 'admin' && showUploadForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[60] p-4 pt-12 sm:pt-4 overflow-y-auto p-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 md:p-8 max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-gray-800">📁 Upload Installation Manual</h2>
            
            <form onSubmit={handleUploadSubmit} className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">PDF File</label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  className="w-full p-2 sm:p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 text-sm"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Manual Title</label>
                  <input
                    type="text"
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                    className="w-full p-2 sm:p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="e.g. SmartJack Installation Guide"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Category</label>
                  <select
                    value={uploadForm.category}
                    onChange={(e) => setUploadForm({...uploadForm, category: e.target.value})}
                    className="w-full p-2 sm:p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Description</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                  className="w-full p-2 sm:p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 h-16 sm:h-20 text-sm"
                  placeholder="Brief description of the installation manual..."
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2 sm:pt-4">
                <button 
                  type="submit" 
                  disabled={loading || !uploadForm.file_data}
                  className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 py-2 sm:py-3 rounded-lg font-bold text-white text-sm sm:text-base"
                >
                  {loading ? 'Uploading...' : 'Upload Manual'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowUploadForm(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 py-2 sm:py-3 rounded-lg font-bold text-white text-sm sm:text-base"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && selectedManual && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-4 pt-12 sm:pt-4 overflow-y-auto">
          <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4 shadow-2xl">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">
              📧 Email Manual: {selectedManual.title}
            </h2>
            
            <form onSubmit={handleSendEmail} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Recipient Email</label>
                  <input
                    type="email"
                    value={emailForm.recipient_email}
                    onChange={(e) => setEmailForm({...emailForm, recipient_email: e.target.value})}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                    placeholder="recipient@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Your Name</label>
                  <input
                    type="text"
                    value={emailForm.sender_name}
                    onChange={(e) => setEmailForm({...emailForm, sender_name: e.target.value})}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                    placeholder="Your Name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Message (Optional)</label>
                <textarea
                  value={emailForm.message}
                  onChange={(e) => setEmailForm({...emailForm, message: e.target.value})}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 h-24"
                  placeholder="Additional message to include with the manual..."
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button 
                  type="submit" 
                  disabled={loading}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 py-3 rounded-lg font-bold text-white"
                >
                  {loading ? 'Sending...' : 'Send Email'}
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowEmailModal(false);
                    setSelectedManual(null);
                  }}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 py-3 rounded-lg font-bold text-white"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Manual Modal */}
      {showEditModal && selectedManual && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[60] p-4 pt-12 sm:pt-4 overflow-y-auto p-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 md:p-8 max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-gray-800">✏️ Edit Manual: {selectedManual.title}</h2>
            
            <form onSubmit={handleUpdateManual} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Manual Title</label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                    className="w-full p-2 sm:p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="e.g. SmartJack Installation Guide"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">Category</label>
                  <select
                    value={editForm.category}
                    onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                    className="w-full p-2 sm:p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  className="w-full p-2 sm:p-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 h-16 sm:h-20 text-sm"
                  placeholder="Brief description of the installation manual..."
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2 sm:pt-4">
                <button 
                  type="submit" 
                  disabled={loading}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 py-2 sm:py-3 rounded-lg font-bold text-white text-sm sm:text-base"
                >
                  {loading ? 'Updating...' : 'Update Manual'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 py-2 sm:py-3 rounded-lg font-bold text-white text-sm sm:text-base"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {currentUser?.rawRole === 'admin' && showDeleteModal && selectedManual && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-[60] p-4 pt-12 sm:pt-4 overflow-y-auto p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
            <h2 className="text-xl font-bold mb-4 text-gray-800">🗑️ Delete Manual</h2>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-2">Are you sure you want to delete this manual?</p>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-semibold text-gray-800">{selectedManual.title}</p>
                <p className="text-sm text-gray-600">{selectedManual.filename}</p>
                <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium mt-1">
                  {selectedManual.category}
                </span>
              </div>
              <p className="text-red-600 text-sm mt-3 font-medium">⚠️ This action cannot be undone.</p>
            </div>

            <div className="flex gap-3">
              <button 
                onClick={handleDeleteManual}
                disabled={loading}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 py-3 rounded-lg font-bold text-white"
              >
                {loading ? 'Deleting...' : 'Delete Manual'}
              </button>
              <button 
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 bg-gray-500 hover:bg-gray-600 py-3 rounded-lg font-bold text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
      )}

    </div>
  );
};

// Gears Landing Page Component

export default InstallationManualsModule;
